import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Plus, MoreVertical, Pencil, Trash2, FolderTree, ChevronRight, ChevronDown, FolderPlus, Search, ArrowUpDown, Download } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to export CSV with auth token
const exportWithAuth = async (endpoint, filename) => {
  const token = localStorage.getItem('token');
  try {
    const response = await fetch(`${API}${endpoint}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    if (!response.ok) {
      throw new Error('Export failed');
    }
    const blob = await response.blob();
    
    // Detect Safari
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    
    // For IE/Edge legacy
    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
      window.navigator.msSaveOrOpenBlob(blob, filename);
      return;
    }
    
    // For Safari - use FileReader to convert to data URL
    if (isSafari) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const link = document.createElement('a');
        link.href = reader.result;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
        }, 250);
      };
      reader.readAsDataURL(blob);
      return;
    }
    
    // For Chrome, Firefox, Edge (Chromium)
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 250);
  } catch (error) {
    console.error('Export error:', error);
    throw error;
  }
};

// Sort options for consistency with other pages
const sortOptions = [
  { value: 'name-asc', label: 'A-Z' },
  { value: 'name-desc', label: 'Z-A' },
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' }
];

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [categoryTree, setCategoryTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name-asc');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState({});
  const { canManageCategories } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    parent_id: null
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const [listResponse, treeResponse] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/categories/tree`)
      ]);
      setCategories(listResponse.data);
      setCategoryTree(treeResponse.data);
      
      // Expand all nodes by default
      const expanded = {};
      const expandAll = (nodes) => {
        nodes.forEach(node => {
          if (node.children && node.children.length > 0) {
            expanded[node.id] = true;
            expandAll(node.children);
          }
        });
      };
      expandAll(treeResponse.data);
      setExpandedNodes(expanded);
    } catch (error) {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  // Sort tree nodes based on sortBy
  const sortNodes = (nodes) => {
    const sorted = [...nodes].sort((a, b) => {
      switch (sortBy) {
        case 'name-asc':
          return a.name.localeCompare(b.name);
        case 'name-desc':
          return b.name.localeCompare(a.name);
        case 'newest':
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        case 'oldest':
          return new Date(a.created_at || 0) - new Date(b.created_at || 0);
        default:
          return 0;
      }
    });
    return sorted.map(node => ({
      ...node,
      children: node.children ? sortNodes(node.children) : []
    }));
  };

  // Filter tree nodes by search term
  const filterNodes = (nodes, term) => {
    if (!term) return sortNodes(nodes);
    
    const filtered = [];
    nodes.forEach(node => {
      const matchesSearch = node.name.toLowerCase().includes(term.toLowerCase()) ||
        (node.description || '').toLowerCase().includes(term.toLowerCase());
      const filteredChildren = node.children ? filterNodes(node.children, term) : [];
      
      if (matchesSearch || filteredChildren.length > 0) {
        filtered.push({
          ...node,
          children: filteredChildren
        });
      }
    });
    return sortNodes(filtered);
  };

  const filteredTree = filterNodes(categoryTree, searchTerm);

  const resetForm = () => {
    setFormData({ name: '', description: '', parent_id: null });
    setEditingCategory(null);
  };

  const handleOpenDialog = (category = null, parentId = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({ 
        name: category.name, 
        description: category.description || '',
        parent_id: category.parent_id || null
      });
    } else {
      resetForm();
      if (parentId) {
        setFormData(prev => ({ ...prev, parent_id: parentId }));
      }
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        parent_id: formData.parent_id === 'none' ? null : formData.parent_id
      };
      
      if (editingCategory) {
        await axios.put(`${API}/categories/${editingCategory.id}`, submitData);
        toast.success('Category updated successfully');
      } else {
        await axios.post(`${API}/categories`, submitData);
        toast.success('Category created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save category');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/categories/${categoryToDelete.id}`);
      toast.success('Category deleted successfully');
      setDeleteDialogOpen(false);
      setCategoryToDelete(null);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    }
  };

  const toggleExpand = (nodeId) => {
    setExpandedNodes(prev => ({
      ...prev,
      [nodeId]: !prev[nodeId]
    }));
  };

  // Render tree node recursively
  const renderTreeNode = (node, depth = 0) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes[node.id];
    
    return (
      <div key={node.id}>
        <div 
          className={`flex items-center py-2 px-3 hover:bg-slate-50 border-b border-slate-100 ${depth > 0 ? 'bg-slate-25' : ''}`}
          style={{ paddingLeft: `${depth * 24 + 12}px` }}
          data-testid={`category-row-${node.id}`}
        >
          {/* Expand/Collapse button */}
          <button
            onClick={() => toggleExpand(node.id)}
            className={`w-6 h-6 flex items-center justify-center mr-2 rounded hover:bg-slate-200 ${!hasChildren ? 'invisible' : ''}`}
          >
            {hasChildren && (isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />)}
          </button>
          
          {/* Category icon */}
          <FolderTree className={`w-4 h-4 mr-2 ${depth === 0 ? 'text-slate-700' : 'text-slate-400'}`} />
          
          {/* Category name and info */}
          <div className="flex-1 min-w-0">
            <span className={`font-medium ${depth === 0 ? 'text-slate-900' : 'text-slate-700'}`}>
              {node.name}
            </span>
            {node.description && (
              <span className="ml-2 text-sm text-slate-400 truncate">
                — {node.description}
              </span>
            )}
          </div>
          
          {/* Level badge */}
          <span className={`text-xs px-2 py-0.5 rounded-full mr-3 ${
            depth === 0 ? 'bg-blue-100 text-blue-700' : 
            depth === 1 ? 'bg-green-100 text-green-700' : 
            'bg-purple-100 text-purple-700'
          }`}>
            {depth === 0 ? 'Root' : `Level ${depth}`}
          </span>
          
          {/* Actions */}
          {canManageCategories() && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleOpenDialog(null, node.id)}>
                  <FolderPlus className="w-4 h-4 mr-2" />
                  Add Sub-category
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleOpenDialog(node)}>
                  <Pencil className="w-4 h-4 mr-2" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-red-600"
                  onClick={() => {
                    setCategoryToDelete(node);
                    setDeleteDialogOpen(true);
                  }}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        
        {/* Children */}
        {hasChildren && isExpanded && (
          <div>
            {node.children.map(child => renderTreeNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  // Get flat list of categories for parent selector (excluding current category and its children)
  const getParentOptions = () => {
    if (!editingCategory) return categories;
    
    // Get all descendant IDs to exclude
    const getDescendantIds = (parentId) => {
      const descendants = [];
      const findChildren = (id) => {
        categories.forEach(cat => {
          if (cat.parent_id === id) {
            descendants.push(cat.id);
            findChildren(cat.id);
          }
        });
      };
      findChildren(parentId);
      return descendants;
    };
    
    const excludeIds = [editingCategory.id, ...getDescendantIds(editingCategory.id)];
    return categories.filter(cat => !excludeIds.includes(cat.id));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="categories-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Categories</h1>
          <p className="text-slate-500 mt-1">Organize your parts with categories and sub-categories</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={async () => {
              const params = new URLSearchParams();
              if (searchTerm) params.append('search', searchTerm);
              try {
                await exportWithAuth(`/categories/export?${params.toString()}`, 'categories_export.csv');
                toast.success('Categories exported successfully');
              } catch (error) {
                toast.error('Failed to export categories');
              }
            }}
            className="rounded-sm"
            data-testid="export-categories-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          {canManageCategories() && (
            <Button
              onClick={() => handleOpenDialog()}
              className="bg-slate-900 hover:bg-slate-800 rounded-sm"
              data-testid="add-category-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Category
            </Button>
          )}
        </div>
      </div>

      {/* Search and Sort */}
      <Card className="border-slate-200 shadow-sm rounded-sm">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                placeholder="Search categories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 rounded-sm"
                data-testid="categories-search-input"
              />
            </div>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-full md:w-40 rounded-sm" data-testid="sort-select">
                <ArrowUpDown className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                {sortOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-sm rounded-sm">
        <CardContent className="p-0">
          {filteredTree.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {filteredTree.map(node => renderTreeNode(node))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <FolderTree className="w-12 h-12 mb-4" />
              <p className="text-lg font-medium">No categories found</p>
              <p className="text-sm">{searchTerm ? 'Try adjusting your search' : 'Create your first category to organize parts'}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="category-dialog">
          <DialogHeader>
            <DialogTitle className="font-bold">
              {editingCategory ? 'Edit Category' : 'Add New Category'}
            </DialogTitle>
            <DialogDescription>
              {editingCategory ? 'Update category details' : 'Create a new category or sub-category'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="parent">Parent Category</Label>
              <Select 
                value={formData.parent_id || 'none'} 
                onValueChange={(value) => setFormData({ ...formData, parent_id: value === 'none' ? null : value })}
              >
                <SelectTrigger className="rounded-sm" data-testid="parent-category-select">
                  <SelectValue placeholder="None (Root Category)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (Root Category)</SelectItem>
                  {getParentOptions().map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.path || cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">Leave empty to create a root category</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="rounded-sm"
                data-testid="category-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="rounded-sm"
                rows={3}
                data-testid="category-description-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">
                Cancel
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="category-submit-btn">
                {editingCategory ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="delete-category-dialog">
          <DialogHeader>
            <DialogTitle className="font-bold text-red-600">Delete Category</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{categoryToDelete?.name}"? 
              Categories with sub-categories or parts cannot be deleted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="rounded-sm">
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} className="rounded-sm" data-testid="confirm-delete-category-btn">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Categories;
